"use client";

import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { brandConfig } from "../../config/brand";
import LootboxOpening from "./LootboxOpening";
import CyberButton from "../ui/CyberButton";
import LootboxPrizeModal from "./LootboxPrizeModal";
import { useRecentPlays, useBoxesCount } from "../../hooks/usePlayExtras";
import { useNeuralToast } from "../../providers/NeuralToastProvider";
import { DepositPromptModal } from "./DepositPromptModal";
import { useAccount, useWriteContract, usePublicClient } from "wagmi";
import { decodeEventLog } from "viem";
import {
  LOOTBOX_CONTRACT_ABI,
  LOOTBOX_CONTRACT_ADDRESS,
  HOLIDAY_HARVEST_CONFIG,
} from "../../config/contract";

type Box = {
  id: string;
  name: string;
  priceLabel: string;
  price: number;
  payment: string;
  src: string;
  glow: string;
  accent: string;
  accentRgb: string;
  videoSrc: string;
  maxQty?: number;
  icon?: string;
  tokenAddress?: string;
  useBlockchain?: boolean;
};

const BOXES: Box[] = [
  {
    id: "harkan",
    name: "HARKAN",
    priceLabel: "FREE",
    price: 0,
    payment: "Free",
    src: "/assets/images/SILVER-BOX.png",
    glow: "rgba(255,255,255,0.28)",
    accent: "#cfd4da",
    accentRgb: "207,212,218",
    videoSrc:
      // "https://pub-5568ed0050fb45079bcdeb4a2a66c3af.r2.dev/BOX_SILVER.mp4",
      "https://customer-dszsedbaiie21p6i.cloudflarestream.com/b619e89192e994ab278326270ab792de/manifest/video.m3u8",
    maxQty: 1,
    useBlockchain: true, // requiere transaction
  },
  // {
  //   id: "gamblor",
  //   name: "GAMBLOR",
  //   priceLabel: "2000 XP",
  //   price: 2000,
  //   payment: "XP",
  //   src: "/assets/images/GAMBLOR-BOX.png",
  //   glow: "rgba(255,215,0,0.35)",
  //   accent: "#f1c40f",
  //   accentRgb: "241,196,15",
  //   videoSrc:
  //     "https://customer-dszsedbaiie21p6i.cloudflarestream.com/d2b28efdd46a01945d918f8c12f58b0e/manifest/video.m3u8",
  //   // "https://pub-5568ed0050fb45079bcdeb4a2a66c3af.r2.dev/gold.mp4",
  //   maxQty: 5,
  //   useBlockchain: true, // requiere transaction
  // },
  // {
  //   id: "gold",
  //   name: "GOLD LOOTBOX",
  //   priceLabel: "200 ASTR",
  //   price: 200,
  //   src: "/assets/images/GOLD-BOX.png",
  //   glow: "rgba(255,215,0,0.35)",
  //   accent: "#f1c40f",
  //   accentRgb: "241,196,15",
  //   videoSrc:
  //     "https://pub-5568ed0050fb45079bcdeb4a2a66c3af.r2.dev/BOX_GOLD.mp4",
  // },
  {
    id: "astr",
    name: "ASTR",
    priceLabel: "1 ASTR",
    price: 1,
    payment: "ASTR",
    src: "/assets/images/SPECTRAL-BOX.png",
    glow: "rgba(180,100,255,0.45)",
    accent: "#b36bff",
    accentRgb: "179,107,255",
    videoSrc:
      "https://customer-dszsedbaiie21p6i.cloudflarestream.com/de7f50a77e84290755781d53995b8b13/manifest/video.m3u8",
    // "https://pub-5568ed0050fb45079bcdeb4a2a66c3af.r2.dev/BOX_SPECTRAL.mp4",
    icon: "/assets/logos/pay-logo.png",
    maxQty: 5,
    useBlockchain: true, // requiere transaction
  },
  // {
  //   id: "hh",
  //   name: "HOLIDAY HARVEST",
  //   priceLabel: "2000 XP",
  //   price: 2000,
  //   payment: "XP",
  //   src: "/assets/images/SPECTRAL-BOX.png",
  //   glow: "rgba(180,100,255,0.45)",
  //   accent: "#b36bff",
  //   accentRgb: "179,107,255",
  //   videoSrc:
  //     "https://customer-dszsedbaiie21p6i.cloudflarestream.com/de7f50a77e84290755781d53995b8b13/manifest/video.m3u8",
  //   // "https://pub-5568ed0050fb45079bcdeb4a2a66c3af.r2.dev/BOX_SPECTRAL.mp4",
  //   maxQty: 2,
  //   useBlockchain: true, // requiere transaction
  // },
];

const getPrizeTypeLabel = (type: string) => {
  type = type.toLowerCase();

  if (type.startsWith("xp_")) {
    return "XP";
  }

  switch (type) {
    case "gamblor_bonus":
      return "GAMBLOR BONUS";
    case "gamblor_spins":
      return "GAMBLOR SPINS";
    case "astr":
      return "ASTR";
    default:
      return type;
  }
};

export default function LootboxSelection({
  context,
}: {
  context: {
    userId: string | null;
    balance: number | null;
    tokenBalances: Record<string, number>;
    startBoxOpening: (box: {
      id: string;
      qty: number;
      payment: string;
      onchainRandomNum?: number | null;
      onchainTxHash?: string | null;
    }) => Promise<void>;
    openingResult: any;
  };
}) {
  const gradient = brandConfig.colors.gradient;
  const [selected, setSelected] = useState<Box>(BOXES[1]);
  const [qty, setQty] = useState<number>(1);
  const [showPrizes, setShowPrizes] = useState(false);
  const [showOpening, setShowOpening] = useState(false);
  const [showDepositPrompt, setShowDepositPrompt] = useState(false);
  const [isWalletTransacting, setIsWalletTransacting] = useState(false);
  const { balance, tokenBalances, startBoxOpening, openingResult } = context;
  const { isConnected } = useAccount();
  const publicClient = usePublicClient();
  const { writeContractAsync, isPending: isContractPending } =
    useWriteContract();

  const { showToast } = useNeuralToast();
  const { plays, loading: loadingPlays } = useRecentPlays();
  const {
    boxCounts,
    resetsAt,
    loading: loadingBoxesCount,
    refetch: refetchBoxesCount,
  } = useBoxesCount(context.userId || undefined);

  const isMobile = window.innerWidth < 768;
  const perf = window.navigator.hardwareConcurrency || 4;
  const qualityFactor = Math.min(1, perf / 8); // scale 0.5–1
  const BACK_COUNT = Math.floor(100 * qualityFactor);
  const FRONT_COUNT = Math.floor(200 * qualityFactor);

  const currentBalance =
    selected.payment === "XP" ? balance : tokenBalances[selected.payment] || 0;

  const boxesRemaining = useMemo(() => {
    return boxCounts[selected.id] || 0;
  }, [boxCounts, selected.id]);

  console.log("boxesRemaining for", selected.id, "is", boxesRemaining);

  const getTimeUntilReset = () => {
    if (!resetsAt) return "unknown time";
    const now = new Date();
    const resetDate = new Date(resetsAt);
    const diffMs = resetDate.getTime() - now.getTime();
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHrs}h ${diffMins}m`;
  };

  const handleBuyNow = async () => {
    if (
      selected.price > 0 &&
      (currentBalance === null || currentBalance < selected.price * qty)
    ) {
      if (selected.payment === "ASTR") {
        setShowDepositPrompt(true);
      } else {
        showToast("INSUFFICIENT FUNDS • Please top up your balance", "error");
      }
      return;
    }

    let onchainRandomNum = null;
    let onchainTxHash = null;

    if (selected.useBlockchain) {
      if (!isConnected) {
        showToast(
          "WALLET NOT CONNECTED • Please connect your wallet first",
          "error"
        );
        return;
      }

      if (!publicClient) {
        showToast("WALLET ERROR • Public client not available", "error");
        return;
      }

      setIsWalletTransacting(true);

      try {
        showToast("CONFIRM IN WALLET • Awaiting signature...", "info");

        onchainTxHash = await writeContractAsync({
          address: LOOTBOX_CONTRACT_ADDRESS,
          abi: LOOTBOX_CONTRACT_ABI,
          functionName: "recordActionWithRandom",
          args: [
            HOLIDAY_HARVEST_CONFIG.gameId,
            HOLIDAY_HARVEST_CONFIG.action,
            HOLIDAY_HARVEST_CONFIG.value,
            HOLIDAY_HARVEST_CONFIG.randomNums,
            HOLIDAY_HARVEST_CONFIG.maxInt,
          ],
        });

        showToast("TRANSACTION SENT • Confirming on blockchain...", "info");

        const receipt = await publicClient.waitForTransactionReceipt({
          hash: onchainTxHash,
        });

        let eventFound = false;

        if (receipt?.logs?.length > 0) {
          for (const log of receipt.logs) {
            if (
              log.address.toLowerCase() !==
              LOOTBOX_CONTRACT_ADDRESS.toLowerCase()
            ) {
              continue;
            }

            try {
              const decoded = decodeEventLog({
                abi: LOOTBOX_CONTRACT_ABI,
                data: log.data,
                topics: log.topics,
              });

              if (decoded.eventName === "GameActionWithRandom") {
                const args = decoded.args as {
                  player: string;
                  gameId: string;
                  action: string;
                  value: bigint;
                  randomNumbers: bigint[];
                  timestamp: bigint;
                };

                if (args.randomNumbers?.length > 0) {
                  onchainRandomNum = Number(args.randomNumbers[0]);
                  eventFound = true;
                  break;
                }
              }
            } catch {
              continue;
            }
          }
        }

        if (!eventFound) {
          throw new Error("Random number event not found in transaction logs");
        }

        console.log(
          "[Wallet] Transaction confirmed, random number:",
          onchainRandomNum
        );

        showToast("BLOCKCHAIN CONFIRMED • Opening lootbox...", "success");
        setIsWalletTransacting(false);
      } catch (error: any) {
        console.error("Wallet transaction failed:", error);
        const errorMessage =
          error?.message?.split("\n")[0] || "Transaction failed";
        showToast(`TRANSACTION FAILED • ${errorMessage.slice(0, 50)}`, "error");
        setIsWalletTransacting(false);
        return;
      }
    }

    try {
      console.log("Attempting to buy box:", selected.id, "Qty:", qty);
      await startBoxOpening({
        id: selected.id,
        qty,
        payment: selected.payment,
        onchainRandomNum,
        onchainTxHash,
      });
      showToast("LOOTBOX OPENING INITIATED • Good luck!", "success");
      console.log("Box opening initiated successfully");
      setShowOpening(true);
      refetchBoxesCount();
    } catch (error) {
      console.error("Error starting box opening:", error);
      showToast("LOOTBOX INIT FAILED • Neural sync disrupted", "error");
    }
  };

  const backParticles = useMemo(() => {
    return Array.from({ length: BACK_COUNT }).map((_, i) => {
      const isSparkle = Math.random() < 0.08; // 8% sparkle chance
      return (
        <div
          key={`back-${i}`}
          className={`particle ${isSparkle ? "sparkle" : ""}`}
          style={
            {
              left: `${5 + Math.random() * 90}%`,
              width: "10px",
              height: "10px",
              opacity: `${0.6 + Math.random() * 0.4}`,
              animationDuration: `${10 + Math.random() * 12}s`,
              animationDelay: `${Math.random() * 9}s`,
              ...(isMobile && { filter: "blur(1.2px)" }),
              "--drift": `${Math.random() * 25 - 12}px`,
            } as React.CSSProperties
          }
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.id]); // 👈 only rebuild when switching lootbox

  const frontParticles = useMemo(() => {
    return Array.from({ length: FRONT_COUNT }).map((_, i) => (
      <div
        key={`front-${i}`}
        className="particle"
        style={
          {
            left: `${5 + Math.random() * 90}%`,
            width: "5px",
            height: "5px",
            opacity: `${0.6 + Math.random() * 0.4}`,
            animationDuration: `${5 + Math.random() * 9}s`,
            animationDelay: `${Math.random() * 7}s`,
            "--drift": `${Math.random() * 20 - 10}px`,
          } as React.CSSProperties
        }
      />
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.id]);

  return (
    <motion.div
      className="relative flex flex-col w-full min-h-screen bg-black md:overflow-hidden md:h-[100dvh]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* HEADER */}
      <div className="z-10 mt-14 md:mt-16  md:mb-10 text-center select-none w-full px-6">
        <p
          className="uppercase tracking-[0.25em] text-xs md:text-sm"
          style={{
            backgroundImage: brandConfig.colors.subtitle,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Open a lootbox & win big prizes
        </p>
        <h2
          className="text-[40px] md:text-[52px] lg:text-[64px] font-extrabold uppercase tracking-tight leading-none"
          style={{
            backgroundImage: gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          OPEN LOOTBOXES
        </h2>
        {/* <p className="mt-2 text-gray-400 text-sm tracking-wide">
          Total prize pool:{" "}
          <span className="text-white font-semibold">1M ASTR</span>
        </p> */}
      </div>

      {/* TOP TRIPTYCH  */}
      <div className="relative z-10 w-full md:flex-1 flex items-center justify-center px-2 md:px-4 py-8 md:py-0 md:min-h-0">
        <div className="flex justify-center items-center gap-2 md:gap-4 lg:gap-6 xl:gap-8 transition-all duration-700 w-full max-w-[1600px] md:h-full">
          {/* 👇 Mobile arrows */}
          <button
            onClick={() => {
              console.log("clicked left");
              setSelected(
                BOXES[
                  (BOXES.findIndex((b) => b.id === selected.id) -
                    1 +
                    BOXES.length) %
                    BOXES.length
                ]
              );
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 md:hidden text-white/60 hover:text-white transition z-99 cursor-pointer h-full flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          {/* Boxes */}
          {BOXES.map((box) => {
            const isSelected = selected.id === box.id;
            const isMobileHidden = selected.id !== box.id;
            return (
              <motion.div
                key={box.id}
                className={`overflow-visible relative flex flex-col items-center justify-center text-center select-none cursor-pointer will-change-transform flex-1 md:max-w-[220px] lg:max-w-[280px] xl:max-w-[320px] 2xl:max-w-[380px] w-full h-full transition-all ${
                  isMobileHidden ? "hidden md:flex" : "flex"
                }`}
                transition={{ type: "spring", stiffness: 220, damping: 18 }}
                onClick={() => {
                  setSelected(box);
                  setQty(1);
                }}
              >
                {/* 🌌 Background particles */}
                {selected.id === box.id && (
                  <div
                    className="particle-bg"
                    style={
                      {
                        "--particle-color": selected.accent,
                        "--particle-rgb": selected.accentRgb,
                      } as React.CSSProperties
                    }
                  >
                    {/* BACK layer (larger, slower) */}
                    {backParticles}

                    {/* FRONT layer (smaller, faster) */}
                    {frontParticles}
                  </div>
                )}

                {/* 🖼️ Box */}
                <motion.img
                  src={box.src}
                  alt={box.name}
                  className="w-[180px] md:w-[160px] lg:w-[200px] xl:w-[240px] 2xl:w-[280px] drop-shadow-[0_20px_40px_rgba(0,0,0,0.55)] z-10"
                  animate={{ scale: isSelected ? 1.05 : 1 }}
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                  whileHover={{ scale: 1.05 }}
                />

                {/* 🌈 Underline */}
                <CyberButton
                  variant={isSelected ? "filled" : "outline"}
                  className="mt-4 px-4 py-1.5 text-xs md:text-sm font-semibold uppercase tracking-wider"
                  borderColor={box.accent}
                >
                  {box.name} {box.priceLabel}
                </CyberButton>
              </motion.div>
            );
          })}

          {/* 👇 Right arrow */}
          <button
            onClick={() => {
              console.log("clicked right");
              setSelected(
                BOXES[
                  (BOXES.findIndex((b) => b.id === selected.id) + 1) %
                    BOXES.length
                ]
              );
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 md:hidden text-white/60 hover:text-white transition z-99 cursor-pointer h-full flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* BOTTOM PANEL */}

      {/* DESKTOP */}
      <div className="hidden relative z-10 w-full px-4 md:px-6 lg:px-8 md:grid grid-cols-1 lg:grid-cols-[minmax(280px,320px)_1fr_minmax(280px,360px)] gap-4 lg:gap-6 border border-white/10 bg-black/30 md:flex-shrink-0 md:max-h-[230px]">
        {/* LEFT: You're buying */}
        <div className="rounded-md p-4 lg:p-6">
          <p className="uppercase tracking-[0.25em] text-[11px] text-[#7fb2ff]">
            You're buying
          </p>
          <h3 className="mt-2 text-xl font-bold text-white">{selected.name}</h3>

          <div className="flex flex-col gap-3 mt-4">
            <div
              onClick={() => console.log("how it works")}
              className="flex items-center gap-2 text-sm font-medium text-transparent bg-gradient-to-r from-[#00DAFF] via-[#B36BFF] to-[#FF0099] bg-clip-text transition-opacity hover:opacity-80 cursor-pointer"
            >
              <img
                src="/assets/icons/info.svg"
                alt=""
                className="w-5 h-5 opacity-80 hover:opacity-100 transition"
              />
              <span>HOW IT WORKS</span>
            </div>

            <div
              onClick={() => setShowPrizes(true)}
              className="flex items-center gap-2 text-sm font-medium text-transparent bg-gradient-to-r from-[#00DAFF] via-[#B36BFF] to-[#FF0099] bg-clip-text transition-opacity hover:opacity-80 cursor-pointer"
            >
              <img
                src="/assets/icons/prizes.svg"
                alt=""
                className="w-5 h-5 opacity-80 hover:opacity-100 transition"
              />
              <span>PRIZE LIST</span>
            </div>
          </div>
        </div>

        {/* CENTER: Quantity + Buy strip */}
        <div className="rounded-md p-4 flex flex-col justify-center lg:p-6">
          <div className="flex flex-row gap-3  items-center ">
            <p className="text-md text-gray-300">Current Balance</p>
            <h3 className="text-3xl font-bold text-white ">
              {currentBalance} {selected.payment}
            </h3>
          </div>
          {/* 
          <div className="flex flex-row xl:grid xl:grid-cols-3 gap-3">
            <CyberFrame variant="outline" className="justify-between w-full">
              <motion.div
                whileTap={{ scale: 0.8 }}
                className="flex items-center gap-2 text-2xl text-transparent bg-gradient-to-r from-[#00DAFF] via-[#B36BFF] to-[#FF0099] bg-clip-text transition-opacity hover:opacity-80 cursor-pointer hover:scale-105"
                onClick={dec}
              >
                –
              </motion.div>

              <div className="px-4 min-w-[48px] flex items-center justify-center text-white relative overflow-hidden text-white">
                <AnimatePresence mode="popLayout">
                  <motion.span
                    key={qty}
                    initial={{ scale: 0.8, opacity: 0.5 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    {qty}
                  </motion.span>
                </AnimatePresence>
              </div>

              <motion.div
                whileTap={{ scale: 0.8 }}
                className="flex items-center gap-2 text-2xl text-transparent bg-gradient-to-r from-[#00DAFF] via-[#B36BFF] to-[#FF0099] bg-clip-text transition-opacity hover:opacity-80 cursor-pointer hover:scale-105"
                onClick={inc}
              >
                +
              </motion.div>
            </CyberFrame>
            <CyberButton
              className="hidden xl:block flex justify-center"
              variant="outline"
              disabled={!!selected.maxQty && selected?.maxQty < 5}
              onClick={() => setPreset(5)}
            >
              Qty: 5
            </CyberButton>
            <CyberButton
              className="hidden xl:block flex justify-center"
              variant="outline"
              disabled={!!selected.maxQty && selected?.maxQty < 10}
              onClick={() => setPreset(10)}
            >
              Qty: 10
            </CyberButton>
          </div> */}

          <CyberButton
            onClick={handleBuyNow}
            icon={selected.icon}
            fullWidth
            className="mt-4"
            disabled={
              loadingBoxesCount ||
              (selected.maxQty ? boxesRemaining === 0 : false) ||
              isWalletTransacting ||
              isContractPending
            }
          >
            {isWalletTransacting || isContractPending
              ? "CONFIRMING..."
              : `BUY NOW - ${selected.price > 0 ? selected.price * qty : ""} ${
                  selected.payment
                }`}
          </CyberButton>

          {/* if max quantity, add the note, if max quantity reached, add a counter until midnight reset (utc) */}
          {!boxesRemaining && (
            <p className="text-sm text-gray-400 mt-2">
              Max quantity reached. Cases resets in {getTimeUntilReset()}.
            </p>
          )}
          {boxesRemaining > 0 && selected.maxQty && (
            <p className="text-sm text-gray-400 mt-2">
              You can buy {boxesRemaining} more of this case.
            </p>
          )}
        </div>

        {/* RIGHT: Recent plays */}
        <div className="rounded-md p-4 lg:p-6">
          <p className="uppercase tracking-[0.25em] text-[11px] text-[#7fb2ff] mb-2">
            Our
          </p>
          <h3 className="text-white font-semibold mb-4">Recent plays</h3>
          {loadingPlays ? (
            <p className="text-gray-500 text-sm animate-pulse">Loading...</p>
          ) : (
            <div className="relative">
              <motion.ul
                key={plays.map((p) => p.username + p.timeAgo).join(",")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="space-y-3 text-sm max-h-[180px] overflow-y-auto scrollbar-hide pr-1"
              >
                {plays.map(
                  (
                    r: {
                      username: string;
                      prizeAmount: number;
                      prizeSymbol: string;
                      prizeType: string;
                      boxType: string;
                      timeAgo: string;
                    },
                    i: number
                  ) => (
                    <li
                      key={i}
                      className="flex items-center justify-between text-gray-300"
                    >
                      <span>
                        <span className="text-white">{r.username}</span> won{" "}
                        <span className="text-[#7fb2ff]">
                          {r.prizeAmount} {getPrizeTypeLabel(r.prizeType)} with{" "}
                          {r.boxType} Case
                        </span>
                      </span>
                      <span className="text-gray-500 text-xs">{r.timeAgo}</span>
                    </li>
                  )
                )}
              </motion.ul>
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-black to-transparent" />
            </div>
          )}
        </div>
      </div>

      {/* MOBILE BOTTOM PANEL */}
      <div className="flex flex-col z-10 w-full px-6 py-4 gap-4 border border-white/10 bg-black md:hidden flex-shrink-0">
        {/* LEFT: You're buying */}
        <div className="rounded-md flex items-center gap-2">
          <p className="uppercase tracking-[0.25em] text-[11px] text-[#7fb2ff]">
            You're buying{" "}
          </p>
          <h3 className="text-lg font-bold text-white">{selected.name}</h3>
        </div>

        <div className="rounded-md">
          <div className="flex flex-row gap-3  items-center ">
            <p className="text-md text-gray-300">Current Balance</p>
            <h3 className="text-xl font-bold text-white ">
              {currentBalance} {selected.payment}
            </h3>
          </div>
          {/* <p className="text-[12px] text-gray-300 mb-2">Quantity</p>
          <div className="flex flex-row md:grid md:grid-cols-3 gap-3">
            <CyberFrame variant="outline" className="justify-between w-full">
              <button
                className="flex items-center gap-2 text-2xl text-transparent bg-gradient-to-r from-[#00DAFF] via-[#B36BFF] to-[#FF0099] bg-clip-text transition-opacity hover:opacity-80 cursor-pointer hover:scale-105"
                onClick={dec}
              >
                –
              </button>

              <div className="px-4 min-w-[48px] flex items-center justify-center text-white relative overflow-hidden text-white">
                {qty}
              </div>

              <button
                className="flex items-center gap-2 text-2xl text-transparent bg-gradient-to-r from-[#00DAFF] via-[#B36BFF] to-[#FF0099] bg-clip-text transition-opacity hover:opacity-80 cursor-pointer hover:scale-105"
                onClick={inc}
              >
                +
              </button>
            </CyberFrame>
          </div> */}

          <CyberButton
            onClick={handleBuyNow}
            icon={selected.icon}
            fullWidth
            className="mt-4"
            disabled={
              loadingBoxesCount ||
              (selected.maxQty ? boxesRemaining === 0 : false) ||
              isWalletTransacting ||
              isContractPending
            }
          >
            {isWalletTransacting || isContractPending
              ? "CONFIRMING..."
              : `BUY NOW - ${selected.price > 0 ? selected.price * qty : ""} ${
                  selected.payment
                }`}
          </CyberButton>
          {!boxesRemaining && (
            <p className="text-sm text-gray-400 mt-2">
              Max quantity reached. Cases resets in {getTimeUntilReset()}.
            </p>
          )}
          {boxesRemaining > 0 && selected.maxQty && (
            <p className="text-sm text-gray-400 mt-2">
              You can buy {boxesRemaining} more of this case.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-3 mt-4">
          <div
            onClick={() => console.log("how it works")}
            className="flex items-center gap-2 text-sm font-medium text-transparent bg-gradient-to-r from-[#00DAFF] via-[#B36BFF] to-[#FF0099] bg-clip-text transition-opacity hover:opacity-80 cursor-pointer"
          >
            <img
              src="/assets/icons/info.svg"
              alt=""
              className="w-5 h-5 opacity-80 hover:opacity-100 transition"
            />
            <span>HOW IT WORKS</span>
          </div>

          <div
            onClick={() => setShowPrizes(true)}
            className="flex items-center gap-2 text-sm font-medium text-transparent bg-gradient-to-r from-[#00DAFF] via-[#B36BFF] to-[#FF0099] bg-clip-text transition-opacity hover:opacity-80 cursor-pointer"
          >
            <img
              src="/assets/icons/prizes.svg"
              alt=""
              className="w-5 h-5 opacity-80 hover:opacity-100 transition"
            />
            <span>PRIZE LIST</span>
          </div>
        </div>

        {/* RIGHT: Recent plays */}
        <div className="rounded-md">
          <p className="uppercase tracking-[0.25em] text-[11px] text-[#7fb2ff] mb-2">
            Our
          </p>
          <h3 className="text-white font-semibold mb-4">Recent plays</h3>
          {loadingPlays ? (
            <p className="text-gray-500 text-sm animate-pulse">Loading...</p>
          ) : (
            <div className="relative">
              <motion.ul
                key={plays.map((p) => p.username + p.timeAgo).join(",")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="space-y-3 text-sm max-h-[180px] overflow-y-auto scrollbar-hide pr-1"
              >
                {plays.map(
                  (
                    r: {
                      username: string;
                      prizeAmount: number;
                      prizeSymbol: string;
                      prizeType: string;
                      boxType: string;
                      timeAgo: string;
                    },
                    i: number
                  ) => (
                    <li
                      key={i}
                      className="flex items-center justify-between text-gray-300"
                    >
                      <span>
                        <span className="text-white">{r.username}</span> won{" "}
                        <span className="text-[#7fb2ff]">
                          {r.prizeAmount} {getPrizeTypeLabel(r.prizeType)} with{" "}
                          {r.boxType} Case
                        </span>
                      </span>
                      <span className="text-gray-500 text-xs">{r.timeAgo}</span>
                    </li>
                  )
                )}
              </motion.ul>
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-black to-transparent" />
            </div>
          )}
        </div>
      </div>

      {/* PRIZE GRID  */}
      <LootboxPrizeModal
        show={showPrizes}
        onClose={() => setShowPrizes(false)}
        selectedBox={selected}
      />

      {showOpening && (
        <LootboxOpening
          openingResult={openingResult}
          boxType={selected.id}
          onEnd={() => setShowOpening(false)}
          videoSrc={selected.videoSrc}
        />
      )}
      <DepositPromptModal
        show={showDepositPrompt}
        onClose={() => setShowDepositPrompt(false)}
      />
    </motion.div>
  );
}
